import React, {useEffect, useState} from 'react';
import {
    Button,
    Modal, ConfigProvider, theme, Tabs, Alert, List, Typography, Row, Col, Grid, Card, Checkbox, Select
} from 'antd';
import {
    SettingOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    LeftOutlined,
    RightOutlined,
    PlusCircleOutlined,
    MinusCircleFilled
} from '@ant-design/icons';
import {breakpointsHandler} from './Util'
import {DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS} from "./GeometryUtil";


export const marginalOptionValues = ["All_Individuals", "Rama", "Rota", "Backbone", "ω", "φ", "ψ", "χ₁", "χ₂", "χ₃", "χ₄", "χ₅"];

const conditionedOptionValues = [ "Rama", "Rota", "Backbone", "ω", "φ", "ψ", "χ₁", "χ₂", "χ₃", "χ₄", "χ₅"];

export const dependentConformationMap = {
    "All_Individuals": ["ω", "φ", "ψ", "χ₁", "χ₂", "χ₃", "χ₄", "χ₅", "Rama", "Rota", "Backbone"],
    "Rama": ["φ", "ψ", "Backbone", "All_Individuals"],
    "Backbone": ["ω", "φ", "ψ", "Rama", "All_Individuals"],
    "Rota": ["χ₁", "χ₂", "χ₃", "χ₄", "χ₅", "All_Individuals"],
    "ω": ["All_Individuals", "Backbone"],
    "φ": ["All_Individuals", "Backbone", "Rama"],
    "ψ": ["All_Individuals", "Backbone", "Rama"],
    "χ₁": ["All_Individuals", "Rota"],
    "χ₂": ["All_Individuals", "Rota"],
    "χ₃": ["All_Individuals", "Rota"],
    "χ₄": ["All_Individuals", "Rota"],
    "χ₅": ["All_Individuals", "Rota"],
}

function MarginalConfiguration({configs, setConfigs, errorIndexes, updateErrorIndexes}) {

    const breakpoints = Grid.useBreakpoint();

    const mapFunc = (currenIndex) => {
        const illegalStates = new Set(currenIndex.flatMap(val => dependentConformationMap[val]))
        return marginalOptionValues.map(optionVal => ({
            label: optionVal,
            value: optionVal,
            disabled: illegalStates.has(optionVal)
        }))
    }

    return (
        <Card>
            <Card.Meta
                title="Marginal Conformations"
                description="Add or delete as you like (populated with the default conformations)"/>
            <br/>
            <List
                dataSource={[...configs, false]}
                renderItem={(conformation, index) => (
                    <List.Item> {
                        (!conformation) ?
                            <Button block type="primary" ghost icon={<PlusCircleOutlined/>} onClick={() => {
                                setConfigs((configs) => ([
                                    ...configs,
                                    []
                                ]))
                            }}>Add New Conformation</Button>
                            : <Row style={{width: '100%'}} gutter={[12, 18]}>
                                <Col xs={3} sm={2} md={2} lg={1} xl={1} xxl={1}
                                     style={{textAlign: 'center', alignContent: "center"}}>
                                    <Typography.Text strong>{index + 1}.</Typography.Text>
                                </Col>
                                <Col xs={21} sm={22} md={22} lg={11} xl={11} xxl={11}
                                     style={{textAlign: 'center', alignContent: "center"}}>
                                    <Select
                                        mode="multiple"
                                        status={errorIndexes.includes(index)?"error":""}
                                        allowClear={true}
                                        value={conformation}
                                        style={{width: '100%'}}
                                        placeholder="Select conformations"
                                        filterOption={true}
                                        onChange={(options) => {
                                            setConfigs((configs) => ([
                                                ...configs.slice(0, index),
                                                options,
                                                ...configs.slice(index + 1, configs.length)
                                            ]))
                                            updateErrorIndexes(errorIndexes=>
                                                errorIndexes.filter(errorIndex=>errorIndex!==index)
                                            )
                                        }}
                                        options={mapFunc(configs[index])}/>
                                </Col>
                                <Col xs={22} sm={23} md={23} lg={11} xl={11} xxl={11}
                                     style={{
                                         textAlign: breakpointsHandler({
                                             xs: 'right',
                                             sm: "right",
                                             md: "right",
                                             lg: "left",
                                             xl: "left",
                                             xxl: "left"
                                         }, breakpoints),
                                         alignContent: "center"
                                     }}>
                                    selected: <LeftOutlined/>{configs[index].join(",")}<RightOutlined/>
                                </Col>
                                <Col xs={1} sm={1} md={1} lg={1} xl={1} xxl={1} style={{textAlign: "right"}}>
                                    <Button
                                        onClick={() => {
                                            setConfigs((configs) => ([
                                                ...configs.slice(0, index),
                                                ...configs.slice(index + 1, configs.length)
                                            ]))
                                            updateErrorIndexes(errorIndexes=>
                                                errorIndexes.filter(errorIndex=>errorIndex!==index)
                                            )
                                        }}
                                        type='text'
                                        icon={<MinusCircleFilled
                                            style={{cursor: 'pointer', color: 'rgb(255 53 53 / 88%)'}}/>}/>
                                </Col>
                            </Row>
                    }
                    </List.Item>
                )}
            />
        </Card>
    )
}

function ConditionalConfiguration({configs, setConfigs, errorIndexes, updateErrorIndexes}) {

    const mapFunc = (currentOptionsList, illegalOptionsList) => {
        const illegalStates = new Set(currentOptionsList.concat(illegalOptionsList).flatMap(val => dependentConformationMap[val]).concat(illegalOptionsList))
        return conditionedOptionValues.map(optionVal => ({
            label: optionVal,
            value: optionVal,
            disabled: illegalStates.has(optionVal)
        }))
    }

    return (
        <Card>
            <Card.Meta
                title="Conditioned conformations"
                description="Add or delete as you like (populated with the default conformations)"/>
            <br/>
            <List
                dataSource={[...configs, false]}
                renderItem={(item, index) => (
                    <List.Item> {
                        (!item) ? <Button block type="primary" ghost icon={<PlusCircleOutlined/>} onClick={() => {
                                setConfigs((configs) => ([
                                    ...configs,
                                    {up: [], down: []}
                                ]))
                            }}>Add New Conformation</Button>
                            : <Row style={{width: '100%'}}>
                                <Col xs={3} sm={2} md={2} lg={1} xl={1} xxl={1}
                                     style={{textAlign: 'center', alignContent: "center"}}>
                                    <Typography.Text strong>{index + 1}.</Typography.Text>
                                </Col>
                                <Col xs={20} sm={21} md={19} lg={16} xl={17} xxl={17}
                                     style={{textAlign: 'left', alignContent: "center",}}>
                                    <Select
                                        style={{width: '40%'}}
                                        status={errorIndexes.includes(index)?"error":""}
                                        placeholder="Select conformations"
                                        mode="multiple"
                                        value={item.up}
                                        onChange={(options) => {
                                            setConfigs((configs) => ([
                                                ...configs.slice(0, index),
                                                {...configs[index], up: options},
                                                ...configs.slice(index + 1, configs.length)
                                            ]))
                                            updateErrorIndexes(errorIndexes=>
                                                errorIndexes.filter(errorIndex=>errorIndex!==index)
                                            )
                                        }}
                                        options={mapFunc(configs[index].up, configs[index].down)}/>
                                    <span style={{paddingLeft: 8, paddingRight: 8, fontWeight:"bold"}}>
                                        given
                                </span>
                                    <Select
                                        style={{width: '40%'}}
                                        mode="multiple"
                                        value={item.down}
                                        status={errorIndexes.includes(index)?"error":""}
                                        placeholder="Select conformations"
                                        onChange={(options) => {
                                            setConfigs((configs) => ([
                                                ...configs.slice(0, index),
                                                {...configs[index], down: options},
                                                ...configs.slice(index + 1, configs.length)
                                            ]))
                                            updateErrorIndexes(errorIndexes=>
                                                errorIndexes.filter(errorIndex=>errorIndex!==index)
                                            )
                                        }}
                                        options={mapFunc(configs[index].down, configs[index].up)}/>
                                </Col>
                                <Col xs={1} sm={1} md={1} lg={1} xl={1} xxl={1} style={{textAlign: "left", alignContent:'center'}}>
                                    <Button type='text' icon={<MinusCircleFilled
                                        style={{cursor: 'pointer', color: 'rgb(255 53 53 / 88%)'}}/>} onClick={() => {
                                        setConfigs((configs) => ([
                                            ...configs.slice(0, index),
                                            ...configs.slice(index + 1, configs.length)
                                        ]))
                                        updateErrorIndexes(errorIndexes=>
                                            errorIndexes.filter(errorIndex=>errorIndex!==index)
                                        )
                                    }}/>
                                </Col>
                            </Row>
                    }
                    </List.Item>
                )}
            />
        </Card>
    )
}

function JointConfiguration({configs, setConfigs}) {
    return (
        <Card>
            <Card.Meta
                title="Joint Conformation"
            />
            <br/>
            <Checkbox onChange={(val) => {
                setConfigs(val.target.checked)
            }} checked={configs}>Include Joint evaluation</Checkbox>
        </Card>
    )
}

function generateValidationMessage(marginalErrorIndexes,conditionalErrorIndexes){
    if (marginalErrorIndexes.length > 0 && conditionalErrorIndexes.length === 0){
        return "Marginal Conformations"
    }

    if (conditionalErrorIndexes.length > 0 && marginalErrorIndexes.length === 0){
        return "Conditioned Conformations"
    }


    if (conditionalErrorIndexes.length > 0 && marginalErrorIndexes.length > 0){
        return "Marginal & Conditioned Conformations"
    }
}


function PdbFormConfiguration({isModalOpen, setModalOpen, advancedQueryBuilder, setAdvancedQueryBuilder, OkButtonText}) {

    const breakpoints = Grid.useBreakpoint();

    const [marginalConfiguration, setMarginalConfiguration] = useState(advancedQueryBuilder.MARGINAL_CONFORMATIONS)

    const [marginalErrorIndexes, setMarginalErrorIndexes] = useState([])

    const [conditionalConfiguration, setConditionalConfiguration] = useState(advancedQueryBuilder.CONDITIONED_CONFORMATIONS)

    const [conditionalErrorIndexes, setConditionalErrorIndexes] = useState([])

    const [jointConfiguration, setJointConfiguration] = useState(advancedQueryBuilder.JOINT_CONFORMATION)


    useEffect(()=>{
        if(isModalOpen){
            setMarginalConfiguration(advancedQueryBuilder.MARGINAL_CONFORMATIONS)
            setConditionalConfiguration(advancedQueryBuilder.CONDITIONED_CONFORMATIONS)
            setJointConfiguration(advancedQueryBuilder.JOINT_CONFORMATION)
        }
    }, [advancedQueryBuilder, isModalOpen])

    const [isAlertOpen, setAlertOpen] = useState(true)

    const tabs = [
        {
            name: "Marginal evaluation",
            component: <MarginalConfiguration
                            configs={marginalConfiguration}
                            setConfigs={setMarginalConfiguration}
                            errorIndexes={marginalErrorIndexes}
                            updateErrorIndexes={setMarginalErrorIndexes}
                    />

        },
        {
            name: "Conditional evaluation" ,
            component: <ConditionalConfiguration
                            configs={conditionalConfiguration}
                            setConfigs={setConditionalConfiguration}
                            errorIndexes={conditionalErrorIndexes}
                            updateErrorIndexes={setConditionalErrorIndexes}
                    />
        },
        {
            name: "Joint evaluation",
            component: <JointConfiguration configs={jointConfiguration} setConfigs={setJointConfiguration}/>
        }

    ]

    return (
        <ConfigProvider theme={{
            ...theme,
            components: {
                ...theme?.components,
                Modal: {
                    ...theme?.components?.Modal,
                    wireframe: true
                }
            }
        }}>
            <Modal
                width={breakpointsHandler({
                    xs: '100%',
                    sm: "90%",
                    md: "80%",
                    lg: "80%",
                    xl: "75%",
                    xxl: "50%"
                }, breakpoints)}
                closable={false}
                title={<span><SettingOutlined style={{paddingRight: 5}}/> Advanced Query Builder </span>}
                open={isModalOpen}
                okText={OkButtonText}
                onOk={()=>{
                    const marginalErrorIndexes =  marginalConfiguration.reduce(
                        (reduced, val, index)=> val.length ===0 ? [...reduced, index]:reduced , []
                    )
                    const conditionalErrorIndexes =  conditionalConfiguration.reduce(
                        (reduced, val, index)=> val.down.length === 0  ||val.up.length === 0  ? [...reduced, index]:reduced , []
                    )
                    if((marginalErrorIndexes + conditionalErrorIndexes).length > 0 ){
                        setConditionalErrorIndexes(conditionalErrorIndexes)
                        setMarginalErrorIndexes(marginalErrorIndexes)
                    }else {
                        setAdvancedQueryBuilder({
                            "JOINT_CONFORMATION": jointConfiguration,
                            "MARGINAL_CONFORMATIONS":marginalConfiguration,
                            "CONDITIONED_CONFORMATIONS":conditionalConfiguration
                        })
                        setModalOpen(false)
                    }
                }}
                onCancel={()=>{setModalOpen(false)}}
            >

                <Alert
                    style={{marginBottom:"min(3vh, 22px)"}}
                    message={isAlertOpen ? <b>Dihedral angle group nomenclature</b> : "Dihedral angle group nomenclature"}
                    type="info"
                    action={
                        <Button size="small" type="text" shape="circle" onClick={() => setAlertOpen(prev => !prev)}>
                            {isAlertOpen ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                        </Button>
                    }
                    showIcon
                    description={isAlertOpen ?
                        <List
                            size="small"
                            dataSource={DIHEDRAL_ANGLE_GROUP_DESCRIPTIONS}
                            renderItem={(item, index) => (
                                <List.Item style={{paddingBottom: 4, paddingTop: (index === 0 ? 6 : 4)}}>
                                    <Row style={{width: '100%'}}>
                                        <Col md={6} lg={5} xl={4} xxl={4}>
                                            <Typography.Text strong style={{fontSize: 15}}>{item[0]}</Typography.Text>
                                        </Col>
                                        <Col md={19} lg={19} xl={20} xxl={20} style={{fontSize: '0.9rem'}}>
                                            {item[1]}
                                        </Col>
                                    </Row>
                                </List.Item>
                            )}
                        /> : ""}/>
                {(marginalErrorIndexes + conditionalErrorIndexes).length > 0 ?
                    <Alert
                        message={`Errors in ${generateValidationMessage(marginalErrorIndexes,conditionalErrorIndexes)}`}
                        type="error"
                        showIcon
                        style={{marginBottom:"min(3vh, 22px)"}}/>
                    :
                    <></>
                }
                <Tabs
                    type="card"
                    items={tabs.map(tab => {
                        return {
                            label: tab.name,
                            key: tab.name,
                            children: tab.component,
                        };
                    })}
                />
            </Modal>
        </ConfigProvider>
    )
}


export default PdbFormConfiguration;